
























































import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import { PaymentSystemPublic } from "@scrile/api-provider/dist/api/PaymentSystemsProvider";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import { AuthorisedOnly } from "@/lib/decorators";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCostByCurrency, getMainCurrencyCode } from "@/lib/moneyHelpers";
import PaymentManager from "@/payments";
import { EVENTS } from "@/const";
import BaseSpinner from "@/components/BaseSpinner";
import ModalView from "@/components/ModalView/ModalView.vue";
import SubscriptionConfirmModal from "./components/SubscriptionConfirmModal.vue";
import SubscriptionItem from "./components/SubscriptionItem.vue";

interface SubscriptionLevelInitialAmount {
  id: string;
  amount: number;
}

@Component({
  name: "UserSubscribeModal",
  components: {
    SubscriptionItem,
    ModalView,
    BaseSpinner,
    SubscriptionConfirmModal,
  },
})
export default class UserSubscribeModal extends mixins(WithAuthUser) {
  @Prop({ type: Object, required: true })
  public readonly user!: UserPublicData;

  @Prop({ type: String, default: $t("Choose subscription") })
  public readonly title!: string;

  @Prop({ type: String })
  public readonly subscriptionLevelId!: string | undefined;

  @Prop({ type: Boolean, required: true })
  public readonly show!: boolean;

  @Prop({ type: Boolean, default: false })
  public readonly outerProcessing!: boolean;

  public processingLevelId: string = "";

  public loading: boolean = false;

  public subscriptionLevels: SubscriptionLevel[] = [];

  public subscriptionLevelsInitialAmount: SubscriptionLevelInitialAmount[] = [];

  public selectedSubscriptionLevel: SubscriptionLevel | null = null;

  public maxCurrentSubscription: SubscriptionLevel | null = null;

  public publicPaymentSystems: PaymentSystemPublic[] | null = null;

  public get filteredSubscriptionLevels(): SubscriptionLevel[] {
    if (!this.subscriptionLevelId) return this.subscriptionLevels;
    const amount =
      this.subscriptionLevels.find(level => level.id === this.subscriptionLevelId)?.pricePackage?.amount ?? 0;
    return this.subscriptionLevels.filter(level => (level?.pricePackage?.amount ?? 0) >= amount);
  }

  get mainCurrency() {
    return getMainCurrencyCode();
  }

  get paymentSystemId() {
    return this.publicPaymentSystems?.find(publicSystem => publicSystem.currencies.includes(this.mainCurrency))?.id;
  }

  get selectedFeedSubscriptionLevelId() {
    if (this.subscriptionLevelId) {
      return this.subscriptionLevelId;
    }
    if (this.subscriptionLevels.length > 0) {
      return this.subscriptionLevels[0].id;
    }
    return "";
  }

  get cost() {
    return this.selectedSubscriptionLevel?.pricePackage
      ? getCostByCurrency(this.selectedSubscriptionLevel?.pricePackage, this.mainCurrency)?.cost
      : 0;
  }

  @AuthorisedOnly
  public async handleSubscribe(selectedSubscriptionLevel: SubscriptionLevel) {
    if (!this.paymentSystemId || this.processingLevelId) return;
    this.selectedSubscriptionLevel = selectedSubscriptionLevel;
    this.processingLevelId = selectedSubscriptionLevel.id;
  }

  public cancelUpgradeLevel() {
    this.selectedSubscriptionLevel = null;
    this.processingLevelId = "";
  }

  public async subscribe(selectedSubscriptionLevel: SubscriptionLevel) {
    try {
      const price = (this.cost && formatMoneyString(this.cost, { currencyCode: this.mainCurrency })) || undefined;
      await PaymentManager.subscribe({
        subscriptionSubject: {
          subjectType: RecurringBillingScheduleSubjectTypeEnum.SUBSCRIPTION,
          subjectId: selectedSubscriptionLevel.id,
        },
        pricePackageId: selectedSubscriptionLevel.pricePackageId,
        price,
      });

      emitter.emit(EVENTS.CONTENT_FEED_PURCHASED, { userId: this.user.id });
      emitter.emit(EVENTS.SUBSCRIPTION_STATUS_CHANGED, { userId: this.user.id, followingStatus: true });

      this.$emit("close", true);
    } finally {
      this.processingLevelId = "";
      this.selectedSubscriptionLevel = null;
    }
  }

  public async getMaxCurrentSubscriptionId() {
    let maxSubscriptionId: string | null = null;
    if (this.authUser) {
      const subscriptions = await providers.SubscriptionProvider.search({
        data: {
          providerId: this.user.id,
          consumerIds: [this.authUser.id],
          expired: false,
        },
      });
      maxSubscriptionId =
        subscriptions.result.length > 0
          ? subscriptions.result.reduce((acc, curr) => (acc.amount > curr.amount ? acc : curr)).levelId
          : null;
    }
    return maxSubscriptionId;
  }

  public async initialAmount(subscriptionLevel: SubscriptionLevel) {
    if (!this.paymentSystemId || !this.authUser) return 0;
    return await providers.SubscriptionUpgradeProvider.initialAmountFind({
      data: {
        currency: this.mainCurrency,
        levelId: subscriptionLevel.id,
        paymentSystemId: this.paymentSystemId,
      },
    });
  }

  public async created() {
    try {
      if (this.user.verified) {
        this.loading = true;
        await providers.PaymentSystemsProvider.findAllPublic().then(r => (this.publicPaymentSystems = r));
        const maxSubscriptionId = await this.getMaxCurrentSubscriptionId();
        await providers.SubscriptionLevelProvider.find({ userIds: [this.user.id] }, [
          "id",
          "userId",
          "pricePackageId",
          { pricePackage: ["amount", { costs: ["cost", "currency"] }] },
          "contentCount",
          "description",
        ]).then(r => {
          if (r.some(level => level.contentCount && level.contentCount > 0)) {
            this.$emit("levelsLoaded", r);
            this.maxCurrentSubscription = r.find(subscription => subscription.id === maxSubscriptionId) ?? null;
            this.subscriptionLevels = r;
          } else {
            this.$emit("levelsLoaded", []);
          }
        });
        for (const subscriptionLevel of this.filteredSubscriptionLevels) {
          if (
            (this.maxCurrentSubscription &&
              subscriptionLevel.pricePackage!.amount > this.maxCurrentSubscription.pricePackage!.amount) ||
            !this.maxCurrentSubscription
          ) {
            const amount = await this.initialAmount(subscriptionLevel);
            this.subscriptionLevelsInitialAmount.push({
              id: subscriptionLevel.id,
              amount,
            });
          }
        }
      } else {
        this.$emit("levelsLoaded", []);
      }
    } finally {
      this.loading = false;
    }
  }
}
